import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, MessageCircle, Car, TreePine, Gamepad2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { contactInfo } from '../utils/clubInfo';
import { prefetchLinkProps } from '../utils/routePrefetch';
import usePadelCourtsSummary from '../hooks/usePadelCourtsSummary';

function FooterLogo({ className = 'h-14 w-auto mb-4' }) {
  return (
    <img
      src="/chantilly-logo.jpg"
      alt="le Chantilly"
      className={`${className} rounded-lg`}
    />
  );
}

const GOOGLE_MAPS_URL = 'https://maps.google.com/?q=1+Rue+Hoche+59491+Villeneuve-d%27Ascq';
const PLAYTOMIC_URL = 'https://playtomic.com/clubs/le-chantilly-club';
const WHATSAPP_URL = 'https://wa.me/33686689103';
const JA_PADEL = 'https://japadel.eu/';

const footerNav = {
  club: [
    { to: '/', label: 'Accueil' },
    { to: '/licences', label: 'Licences' },
    { to: '/contact', label: 'Contact' },
    { to: '/restaurant', label: 'Restaurant' },
  ],
  activites: [
    { to: '/padel', label: 'Padel' },
    { to: '/fitness', label: 'Fitness' },
    { to: '/loisirs', label: 'Loisirs & Flippers' },
    { to: '/badminton', label: 'Badminton' },
    { to: '/basket', label: 'Basket' },
    { to: '/proshop', label: 'Proshop' },
  ],
  tournois: [
    { to: '/tournois', label: 'Vue d\'ensemble' },
    { to: '/tournois-padel', label: 'Tournois Padel' },
    { to: '/tournois-flippers', label: 'Tournois Flippers' },
  ],
  entreprises: [
    { to: '/entreprises', label: 'Offres B2B' },
    { to: '/seminaires', label: 'Séminaires' },
    { to: '/team-building', label: 'Team Building' },
    { to: '/privatisation', label: 'Privatisation' },
  ],
};

const Footer = () => {
  const padel = usePadelCourtsSummary();

  return (
  <footer className="bg-chantilly-green-dark border-t border-chantilly-green-light/30">
    <div className="container mx-auto px-4 py-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
        <div className="lg:col-span-2">
          <FooterLogo className="h-14 w-auto mb-4" />
          <p className="text-chantilly-cream/70 mb-4 text-sm leading-relaxed">
            <strong className="text-chantilly-cream">le Chantilly</strong> — Sport, jeu et restauration à Villeneuve-d&apos;Ascq.
            {' '}{padel.shortLabel} ({padel.subLabel}), 20 flippers d&apos;exception, +200 tournois homologués par saison.
          </p>
          <div className="flex gap-3">
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-chantilly-cream/70 hover:text-chantilly-gold transition-colors" aria-label="Google Maps">
              <MapPin className="w-4 h-4" />
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-chantilly-cream/70 hover:text-chantilly-gold transition-colors" aria-label="WhatsApp">
              <MessageCircle className="w-4 h-4" />
            </a>
            <a href={PLAYTOMIC_URL} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-chantilly-cream/70 hover:text-chantilly-gold transition-colors" aria-label="Playtomic">
              <ExternalLink className="w-4 h-4" />
            </a>
            <a href={JA_PADEL} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-chantilly-cream/70 hover:text-chantilly-gold transition-colors" aria-label="JA Padel">
              <Gamepad2 className="w-4 h-4" />
            </a>
          </div>
        </div>

        {[
          { title: 'Le club', links: footerNav.club },
          { title: 'Activités', links: footerNav.activites },
          { title: 'Tournois', links: footerNav.tournois },
          { title: 'Entreprises', links: footerNav.entreprises },
        ].map((col) => (
          <div key={col.title}>
            <h4 className="text-chantilly-cream font-semibold mb-4">{col.title}</h4>
            <ul className="space-y-2">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-chantilly-cream/65 hover:text-chantilly-gold text-sm transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 pt-8 border-t border-chantilly-green-light/30 text-sm text-chantilly-cream/70">
        <div className="space-y-2">
          <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:text-chantilly-gold">
            <MapPin className="w-4 h-4 text-chantilly-gold shrink-0 mt-0.5" />
            <span>{contactInfo.address.street}, {contactInfo.address.city}</span>
          </a>
          <a href={`tel:${contactInfo.phones.main.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-chantilly-gold">
            <Phone className="w-4 h-4 text-chantilly-gold" />
            {contactInfo.phones.main}
          </a>
          <a href={`mailto:${contactInfo.emails.general}`} className="flex items-center gap-2 hover:text-chantilly-gold">
            <Mail className="w-4 h-4 text-chantilly-gold" />
            {contactInfo.emails.general}
          </a>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-chantilly-gold" /> Lun–Ven : 08h–00h</div>
          <div className="ml-6">Sam–Dim : 09h–20h</div>
          <div className="flex items-center gap-2 mt-2"><Car className="w-4 h-4 text-chantilly-gold" /> Parking 100 places</div>
          <div className="flex items-center gap-2"><TreePine className="w-4 h-4 text-chantilly-gold" /> Terrasse vue forêt</div>
        </div>
        <div className="flex flex-col gap-2">
          <a href={PLAYTOMIC_URL} target="_blank" rel="noopener noreferrer">
            <Button className="w-full bg-chantilly-gold text-chantilly-green-dark hover:bg-chantilly-gold-dark font-bold text-sm">
              Réserver padel
            </Button>
          </a>
          <Button className="w-full bg-chantilly-cream text-chantilly-green-dark hover:bg-white font-bold text-sm" onClick={() => window.open(JA_PADEL, '_blank')}>
            Tournois JA Padel
          </Button>
        </div>
      </div>

      <div className="border-t border-chantilly-green-light/30 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-chantilly-cream/55 text-sm">
        <span>© 2026 le Chantilly. Tous droits réservés.</span>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/mentions-legales" className="hover:text-chantilly-gold">Mentions légales</Link>
          <Link to="/politique-confidentialite" className="hover:text-chantilly-gold">Confidentialité</Link>
          <Link to="/cgv" className="hover:text-chantilly-gold">CGV</Link>
        </div>
      </div>
    </div>
  </footer>
  );
};

export default React.memo(Footer);
